import React, { memo, SyntheticEvent, useCallback, useMemo } from 'react';

import MuiAutocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormLabel from '@mui/material/FormLabel';
import { InputProps } from '@mui/material/Input';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';

import {
  useWindowedTooltipZIndex,
  withWindowedPopperProps,
} from '../dialog/windowed-tooltip-zindex-context';
import { TextField, TextFieldProps } from '../text-field/text-field';

type Option = { id: string | number; label: string; value: string };

type SelectProps<T extends Option> = Omit<TextFieldProps, 'onChange'> & {
  value?: string;
  options?: T[];
  onChange: (value: string) => void;
  isLoading?: boolean;
};

const BaseAutocomplete = <T extends Option>({
  id,
  required,
  value,
  onChange,
  onBlur,
  label,
  helperText,
  error,
  fullWidth,
  disabled,
  options,
  size,
  isLoading,
}: SelectProps<T>) => {
  const windowedConfig = useWindowedTooltipZIndex();
  const { optionsMap, optionValues } = useMemo(() => {
    return {
      optionsMap: keyBy(options, 'value') as Record<string, Option>,
      optionValues: map(options, (item) => item.value) as string[],
    };
  }, [options]);

  const getOptionLabel = useCallback(
    (option: string) => get(optionsMap, [option, 'label']) || '',
    [optionsMap]
  );

  const isOptionEqualToValue = useCallback(
    (option: string, value: string) => option === value,
    []
  );

  const handleChange = useCallback(
    (event: SyntheticEvent, value: string | null) => {
      onChange(value || '');
    },
    [onChange]
  );

  const extraProps: Partial<InputProps> = useMemo(() => {
    if (isLoading) {
      return {
        startAdornment: (
          <Box pl={2} pt={1}>
            <CircularProgress size={14} />
          </Box>
        ),
      };
    }
    return {};
  }, [isLoading]);

  return (
    <Box>
      <FormLabel>
        {label} {required && <span style={{ color: 'red' }}> * </span>}
      </FormLabel>
      <MuiAutocomplete
        id={id}
        size={size}
        value={value as any}
        disabled={disabled}
        fullWidth={fullWidth}
        options={optionValues ?? []}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        onChange={handleChange}
        slotProps={{
          popper: withWindowedPopperProps(windowedConfig, undefined),
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            required={required}
            helperText={helperText}
            error={error}
            onBlur={onBlur}
            InputProps={{ ...params.InputProps, ...extraProps }}
          />
        )}
      />
    </Box>
  );
};

export const Autocomplete = memo(BaseAutocomplete);
export default Autocomplete;
